import getScheduledBreaks, { Props } from '@sdk/automatedBreaks/helpers/getScheduledBreaks';
import displayDuration from '@sdk/utils/displayDuration';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { AutomatedBreakType } from '@sdk/settings/types';
import { BreakViolation } from '@sdk/automatedBreaks/types/BreakViolation';

import multipleBreaksViolationsToText from '@web/timeClock/utils/multipleBreaksViolationsToText';

export const getMealBreakSummary = (numberOfMealBreaks: number) =>
  numberOfMealBreaks
    ? ngettext('%(meal)s meal break', '%(meal)s meal breaks', numberOfMealBreaks, {
        meal: numberOfMealBreaks,
      })
    : '';

export const getMealBreakViolationsSummary = (violationReasons: Record<BreakViolation, number>) =>
  Object.keys(violationReasons)
    .reduce((acc, reason) => {
      const count = violationReasons[reason];
      const getViolationMessage = multipleBreaksViolationsToText[reason];
      acc.push(getViolationMessage(AutomatedBreakType.meal, count));
      return acc;
    }, [])
    .join(' ·\n');

export const getRestBreakSummary = (numberOfRestBreaks: number) =>
  numberOfRestBreaks
    ? ngettext('%(rest)s rest break', '%(rest)s rest breaks', numberOfRestBreaks, {
        rest: numberOfRestBreaks,
      })
    : '';

export const getRestBreakViolationsSummary = (violationReasons: Record<BreakViolation, number>) =>
  Object.keys(violationReasons)
    .reduce((acc, reason) => {
      const count = violationReasons[reason];
      const getViolationMessage = multipleBreaksViolationsToText[reason];
      acc.push(getViolationMessage(AutomatedBreakType.rest, count));
      return acc;
    }, [])
    .join(' ·\n');

export const getMissedMealBreakSummary = (numberOfMealBreaks: number) =>
  numberOfMealBreaks
    ? ngettext('%(meal)s missed meal break', '%(meal)s missed meal breaks', numberOfMealBreaks, {
        meal: numberOfMealBreaks,
      })
    : '';

export const getMissedRestBreakSummary = (numberOfRestBreaks: number) =>
  numberOfRestBreaks
    ? ngettext('%(rest)s missed rest break', '%(rest)s missed rest breaks', numberOfRestBreaks, {
        rest: numberOfRestBreaks,
      })
    : '';

export const getWaivedMealBreakSummary = (numberOfMealBreaks: number) =>
  numberOfMealBreaks
    ? ngettext('%(meal)s waived meal break', '%(meal)s waived meal breaks', numberOfMealBreaks, {
        meal: numberOfMealBreaks,
      })
    : '';

export const getWaivedRestBreakSummary = (numberOfRestBreaks: number) =>
  numberOfRestBreaks
    ? ngettext('%(rest)s waived rest break', '%(rest)s waived rest breaks', numberOfRestBreaks, {
        rest: numberOfRestBreaks,
      })
    : '';

const getStandardBreakSummary = (breakDuration?: number) => {
  if (!breakDuration) {
    return '';
  }

  return gettext('%(breakDuration)s break', {
    breakDuration: displayDuration(breakDuration),
  });
};

const getScheduledBreaksSummary = ({
  shift,
  automatedBreaksSettings,
  automatedBreaksFeatureFlag,
}: Props & { automatedBreaksFeatureFlag: boolean }) => {
  if (!automatedBreaksFeatureFlag || !automatedBreaksSettings.isEnabled) {
    return getStandardBreakSummary(shift.breakDuration);
  }

  const { meal, rest } = getScheduledBreaks({ shift, automatedBreaksSettings });
  let result = '';
  if (meal) {
    result = getMealBreakSummary(meal);
  }

  if (rest) {
    const restBreak = getRestBreakSummary(rest);
    result = meal ? `${result} · ${restBreak}` : restBreak;
  }

  return result;
};

export default getScheduledBreaksSummary;
